import Select, { DefaultOptionType, SelectProps } from 'antd/lib/select';
import IconTooltip from 'components/ui/base/tooltip/IconTooltip';
import { FieldHelperProps, FieldInputProps, FieldMetaProps } from 'formik';
import useDeclarationInputFocused from 'hooks/useDeclarationInputFocused';
import { FC, FocusEvent, ReactNode, UIEvent, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Option } from '../../../base/select/Select';
import {
    ErrorDiv,
    ErrorLabel,
    FieldWrapper,
    FormItem,
    InputDiv,
    InputLabel,
    InputLabelRequired,
    InputSection,
    LabelDiv,
    LabelSpacing,
    MetaSection,
    RefDiv,
} from '../formInput/DeclarationInput.styles';
import { handleBlur } from '../utils';
import { StyledDeclarationSelect } from './DeclarationSelect.styles';
import { getFormikProps, toPlaceholder } from 'views/declarations/utils/form-utils';
import MultipleButtons from '../MultipleButtons';
import { useLocation, useOutletContext } from 'react-router-dom';
import { TemplateCheckboxes } from 'components/ui/composed/template/TemplateCheckbox';
import { useTemplateContext } from '../../template/TemplateContext';
import { useRegisterField } from '../../template/CardContext';
import SearchCustomerButton from 'views/declarations/common/search-customer/SearchCustomerButton';
import useFieldTemplateMetaData from '../../template/useTemplateViewEditControls';
import useNonAmendableField from 'views/declarations/utils/useNonAmendableField';
import { Divider, Space } from 'antd';
import CodelistMenuDrawer from './CodelistMenuDrawer';
import useDeclarations from 'hooks/useDeclarations';
import { Validator } from 'views/declarations/uk/export/validations/validations';
import { kebabCase } from 'lodash';
import { TestProps } from 'core/utils/testTypes';
import GvmsCustomFieldsDrawerButton from '../../../../../views/declarations/uk/gvms/components/GvmsCustomFieldsDrawerButton';
import { GvmsFormCustomFields } from '../../../../../views/declarations/uk/gvms/enums';
import { TextSmall } from '../../../base/typography';
import EllipsisTextTooltip from '../../../../EllipsisTextTooltip';
import { StyledInfoCircleOutlined } from 'views/declarations/declaration-tabs/declaration-form/Form.styles';
import { DeclarationTypes } from 'store/declarations/enums/common/declaration-types';
import useSelectOptions from './useSelectOptions';
import { useDeclarationValueSource } from '../source/DeclarationSourceContext';
import DeclarationValueSourceIcon from '../source/DeclarationValueSourceIcon';
import SourceIconPrefix from '../source/SourceIconPrefix';

const { OptGroup } = Select;

export interface SelectOption {
    id: string;
    value: string;
    disabled?: boolean;
}

export interface MultipleProps {
    node: ReactNode;
}

export type GroupSelectOption = Record<string, Readonly<SelectOption[]>>;

export type FormSelectProps = SelectProps &
    TestProps & {
        labelSpacing?: LabelSpacing;
        label?: string;
        tooltip?: string;
        icon?: ReactNode;
        refNumber?: string | string[];
        selectOptions?: Readonly<SelectOption[]> | Readonly<GroupSelectOption>;
        codelistProps?: {
            codelistName: string;
            declarationType: DeclarationTypes.ICS2;
            // Limits CL244 (UN/LOCODE) results to one country
            countryCode?: string;
        };
        children?: ReactNode;
        refClicked?: (refNumber: string | number) => void;
        fieldProps: FieldInputProps<any>;
        fieldMeta?: FieldMetaProps<any>;
        fieldHelper?: FieldHelperProps<any>;
        required?: boolean;
        multiple?: MultipleProps;
        hideKeys?: boolean;
        viewOnly?: boolean;
        disabled?: boolean;
        amendment?: boolean;
        canLoadMore?: boolean;
        onLoadMore?: () => void;
        fieldEvents?: {
            onChange?: (value: any, option: DefaultOptionType | DefaultOptionType[]) => void;
            onBlur?: (e: FocusEvent<HTMLInputElement> | FocusEvent<HTMLElement>) => void;
        };
        onPopupScroll?: (event: UIEvent<HTMLDivElement>) => void;
        onClear?: () => void;
        condensed?: boolean;
        multipleF?: {
            onAdd: () => void;
            onRemove: () => void;
            canAdd?: boolean;
            canRemove?: boolean;
        };
        onlyKeys?: boolean;
        specialName?: string; // Used for template meta handle of taxBox34Bis fields as of 13/04/23
        hidden?: boolean;
        elementLocator?: string;
        displayAsteriskOnRight?: boolean;
        /**
         * Keep the caller's `value` and `onChange` when this renders inside the template
         * builder, instead of taking both from the template formik.
         *
         * For a field whose stored shape differs from what the select emits, the caller passes
         * an adapter pair on `fieldProps` (see `CountriesOfRoutingField`, which stores
         * `{ sequenceNumber, country }[]` but selects plain codes). Without this the adapter is
         * dropped and the raw selection is written straight to the template, in the wrong shape.
         * The path, meta and view/edit controls are unaffected — they still key off `name`, so
         * the caller is responsible for writing to the template path itself (`useTemplatePath`).
         */
        keepFieldPropsInTemplate?: boolean;
    } & (
        | { hideCodelistMenu: boolean; codeValidation?: Validator[] }
        | { hideCodelistMenu?: boolean; codeValidation: Validator[] }
    );

export interface SelectSearchOption {
    children: any;
    key: string;
    props: any;
    value: string;
}

export const hideAllDropdowns = () => {
    const selectDropdowns = document.querySelectorAll('.ant-select-dropdown');
    if (selectDropdowns) selectDropdowns.forEach((dropdown: any) => (dropdown.style.visibility = 'hidden'));
};
export const showAllDropdowns = () => {
    const selectDropdowns = document.querySelectorAll('.ant-select-dropdown');
    if (selectDropdowns) selectDropdowns.forEach((dropdown: any) => (dropdown.style.visibility = 'visible'));
};

const DeclarationSelect: FC<FormSelectProps> = ({
    labelSpacing = 'none',
    label,
    refNumber: refNumberProp,
    tooltip,
    selectOptions,
    fieldProps: fieldPropsProp,
    fieldMeta: fieldMetaProp,
    fieldHelper: fieldHelpersProp,
    multiple,
    refClicked,
    required,
    hideKeys,
    viewOnly: viewOnlyProp,
    disabled,
    fieldEvents,
    onClear: handleClear,
    condensed,
    multipleF,
    onlyKeys,
    specialName,
    hideCodelistMenu,
    codeValidation,
    hidden,
    testId,
    canLoadMore,
    onLoadMore,
    onPopupScroll,
    elementLocator,
    displayAsteriskOnRight,
    codelistProps,
    keepFieldPropsInTemplate,
    ...selectProps
}) => {
    const [codelistMenuDrawer, setCodelistMenuDrawer] = useState<boolean | undefined>(undefined);
    const { t } = useTranslation('common');
    const { setFocused } = useDeclarationInputFocused();
    const outletContext = useOutletContext<{
        amendment?: boolean;
    }>();
    const location = useLocation();
    const inViewOnly = location.pathname.split('/').includes('view-only');
    const { template, templateFormik, form, isViewOnly: isTemplateViewOnly } = useTemplateContext();
    const { declaration } = useDeclarations();

    useRegisterField({ path: fieldPropsProp?.name, required, hidden });
    const { isViewable, isEditable, isInvisible } = useFieldTemplateMetaData(fieldPropsProp?.name);
    const isNonAmendable = useNonAmendableField(fieldPropsProp?.name);

    const { fieldProps, fieldMeta, fieldHelpers } = useMemo(() => {
        if (template && templateFormik && form) {
            const f = getFormikProps(`${form}.defaults.${fieldPropsProp?.name}`, templateFormik);
            return {
                ...f,
                fieldProps: keepFieldPropsInTemplate
                    ? { ...f.fieldProps, value: fieldPropsProp?.value, onChange: fieldPropsProp?.onChange }
                    : f.fieldProps,
                fieldHelpers: f.fieldHelper,
            };
        }

        return { fieldProps: fieldPropsProp, fieldMeta: fieldMetaProp, fieldHelpers: fieldHelpersProp };
    }, [template, templateFormik, form, keepFieldPropsInTemplate, fieldPropsProp, fieldMetaProp, fieldHelpersProp]);

    const valueSource = useDeclarationValueSource(fieldPropsProp?.name, fieldMeta);
    const sourceIcon = useMemo(
        () => (valueSource ? <DeclarationValueSourceIcon {...valueSource} /> : undefined),
        [valueSource]
    );

    const { options, loading, searchProps } = useSelectOptions({
        codelistProps,
        selectOptions,
        fieldValue: fieldProps.value,
    });

    const handleRefClick = useCallback(() => {
        if (Array.isArray(refNumberProp)) return;

        if (refClicked) {
            refNumberProp ? refClicked(refNumberProp) : refClicked(label ?? '');
        }
    }, [refClicked, refNumberProp, label]);

    const handleEmptyValues = useCallback((item: SelectOption) => {
        return !!item.value ? item.value : item.id;
    }, []);

    const handleShowCodelistMenuDrawer = useCallback(() => {
        setCodelistMenuDrawer(true);

        hideAllDropdowns();
    }, []);

    const hideCodelistMenuDrawer = useCallback(() => {
        showAllDropdowns();

        setCodelistMenuDrawer(false);
    }, []);

    const onMouseClicked = (e: React.MouseEvent<Element>) => {
        e.stopPropagation();
        e.preventDefault();
    };

    const showTooltip = useMemo(() => tooltip && tooltip.length > 0, [tooltip]);

    const viewOnly = useMemo(() => {
        if (template) return isTemplateViewOnly;
        if (outletContext?.amendment) return isViewable;
        return viewOnlyProp || !isEditable || inViewOnly || declaration?.archived;
    }, [
        template,
        isTemplateViewOnly,
        outletContext?.amendment,
        isViewable,
        viewOnlyProp,
        isEditable,
        inViewOnly,
        declaration?.archived,
    ]);

    const optionsRender = useMemo(() => {
        if (!options) {
            return null;
        }

        if (Array.isArray(options)) {
            return options?.map((item: SelectOption, i: number) =>
                onlyKeys ? (
                    <Option onClick={onMouseClicked} key={i} value={item.id} disabled={item.disabled}>
                        {item.id}
                    </Option>
                ) : !hideKeys ? (
                    <Option onClick={onMouseClicked} key={i} value={item.id} disabled={item.disabled}>
                        {`${item.id}${item.value ? ' - ' + item.value : ''}`}
                    </Option>
                ) : (
                    <Option onClick={onMouseClicked} key={i} value={item.id} disabled={item.disabled}>
                        {handleEmptyValues(item)}
                    </Option>
                )
            );
        }

        return Object.entries(options as Readonly<GroupSelectOption>).map(([group, options]) => {
            return (
                <OptGroup label={group}>
                    {options?.map((item: SelectOption, i: number) =>
                        !hideKeys ? (
                            <Option onClick={onMouseClicked} key={i} value={item.id}>
                                {`${item.id} - ${item.value}`}
                            </Option>
                        ) : (
                            <Option onClick={onMouseClicked} key={i} value={item.id}>
                                {handleEmptyValues(item)}
                            </Option>
                        )
                    )}
                </OptGroup>
            );
        });
    }, [handleEmptyValues, hideKeys, onlyKeys, options]);

    const refNumber = useMemo(() => {
        if (Array.isArray(refNumberProp)) {
            return (
                <>
                    {refNumberProp.map((ref) => (
                        <RefDiv key={`${label}-${ref}`}>{ref}</RefDiv>
                    ))}
                </>
            );
        } else if (typeof refNumberProp === 'string') {
            return <RefDiv>{refNumberProp}</RefDiv>;
        }
    }, [refNumberProp, label]);

    const hasMeta = useMemo(() => !!label || !!refNumberProp || showTooltip, [label, refNumberProp, showTooltip]);

    const fillButtonsDisabled = useMemo(
        () => template && !isEditable && !isViewable,
        [isEditable, isViewable, template]
    );
    const handleTemplateCheckboxChange = useMemo(() => {
        return fieldEvents?.onChange
            ? (value: any) => fieldEvents.onChange!(value, [])
            : selectProps.onChange
            ? (value: any) => {
                  fieldProps.onChange({ target: { value, name: fieldProps.name } });
                  selectProps.onChange!(value, []);
              }
            : undefined;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fieldEvents?.onChange, fieldProps.onChange, selectProps.onChange]);

    const viewOnlyValues = useMemo(() => {
        // `options` covers both preloaded `selectOptions` and codelists fetched via `codelistProps`.
        const selectedCodelist = Array.isArray(options)
            ? options.find((o: { id: string }) => o.id === fieldPropsProp?.value)
            : undefined;
        const value = selectedCodelist ? `${selectedCodelist.id} - ${selectedCodelist.value}` : '-';

        return {
            value,
            tooltipTitle: selectedCodelist?.value,
        };
    }, [fieldPropsProp?.value, options]);

    const handlePopupScroll = (event: UIEvent<HTMLDivElement>) => {
        const target = event.currentTarget as HTMLDivElement;
        const { scrollTop, scrollHeight, clientHeight } = target;

        if (scrollTop + clientHeight + 100 >= scrollHeight && canLoadMore) {
            onLoadMore?.();
        }
    };

    const id = `select-id-${fieldProps.name}`;

    const render = useMemo(
        () => (
            <FieldWrapper $viewOnly={viewOnly} $template={template}>
                {viewOnly ? (
                    <>
                        {hasMeta && (
                            <MetaSection>
                                <LabelDiv condensed={condensed} $labelSpacing={labelSpacing}>
                                    {refNumber}

                                    {label && (
                                        <InputLabel htmlFor={id}>
                                            {!displayAsteriskOnRight && required && (
                                                <InputLabelRequired right>*</InputLabelRequired>
                                            )}
                                            {label}
                                            {displayAsteriskOnRight && required && (
                                                <InputLabelRequired>*</InputLabelRequired>
                                            )}
                                        </InputLabel>
                                    )}

                                    {showTooltip && (
                                        <IconTooltip
                                            title={label}
                                            tooltip={tooltip ?? t('defaultTooltip')}
                                            icon={<StyledInfoCircleOutlined />}
                                            tooltipClicked={handleRefClick}
                                        />
                                    )}
                                </LabelDiv>
                                {template && <TemplateCheckboxes disabled fieldPath={fieldPropsProp?.name} />}
                            </MetaSection>
                        )}
                        <InputSection>
                            <EllipsisTextTooltip tooltipTitle={viewOnlyValues.tooltipTitle}>
                                <TextSmall
                                    data-testid={testId ?? kebabCase(label ?? fieldPropsProp?.name)}
                                    style={{
                                        display: 'block',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                    }}
                                >
                                    {viewOnlyValues?.value}
                                </TextSmall>
                            </EllipsisTextTooltip>
                        </InputSection>
                    </>
                ) : (
                    <>
                        {hasMeta && (
                            <MetaSection>
                                <LabelDiv condensed={condensed} $labelSpacing={labelSpacing}>
                                    {refNumber}

                                    {label && (
                                        <InputLabel htmlFor={id}>
                                            {!displayAsteriskOnRight && required && (
                                                <InputLabelRequired right>*</InputLabelRequired>
                                            )}
                                            {label}
                                            {displayAsteriskOnRight && required && (
                                                <InputLabelRequired>*</InputLabelRequired>
                                            )}
                                        </InputLabel>
                                    )}

                                    {showTooltip && (
                                        <IconTooltip
                                            title={label}
                                            tooltip={tooltip ?? t('defaultTooltip')}
                                            icon={<StyledInfoCircleOutlined />}
                                            tooltipClicked={handleRefClick}
                                        />
                                    )}
                                </LabelDiv>
                            </MetaSection>
                        )}
                        <InputSection>
                            <FormItem validateStatus={fieldMeta?.error && !!fieldMeta?.touched ? 'error' : ''}>
                                <InputDiv>
                                    <div style={{ flex: '1', maxWidth: '100%', width: '100%', minWidth: '0' }}>
                                        <SourceIconPrefix icon={sourceIcon}>
                                            <StyledDeclarationSelect
                                                id={id}
                                                lock={!!template}
                                                disabled={disabled || isInvisible || isNonAmendable}
                                                placeholder={
                                                    selectProps.placeholder ?? toPlaceholder(label, 'Select') ?? ''
                                                }
                                                showSearch
                                                // Disable browser autofill triggering the select options
                                                autoComplete="one-time-code"
                                                loading={loading}
                                                notFoundContent={loading ? 'Loading...' : undefined}
                                                allowClear
                                                onClear={handleClear}
                                                testId={testId ?? kebabCase(label ?? fieldPropsProp?.name)}
                                                elementLocator={elementLocator}
                                                {...fieldProps}
                                                status={fieldMeta?.error && !!fieldMeta?.touched ? 'error' : ''}
                                                filterOption={searchProps.filterOption}
                                                onSearch={searchProps.onSearch}
                                                searchValue={searchProps.searchValue}
                                                onChange={(value, option) => {
                                                    fieldProps?.onChange({ target: { value, name: fieldProps.name } });
                                                    selectProps.onChange?.(
                                                        { target: { value, name: fieldProps.name } },
                                                        option
                                                    );
                                                }}
                                                {...fieldEvents}
                                                onBlur={(e) => {
                                                    fieldHelpers?.setTouched(true);
                                                    handleBlur(setFocused, fieldProps, e);
                                                    selectProps?.onBlur?.(e);
                                                    fieldEvents?.onBlur?.(e);
                                                }}
                                                value={selectProps.value ?? fieldProps.value}
                                                onClick={onMouseClicked}
                                                onFocus={() => setFocused(true)}
                                                onDropdownVisibleChange={searchProps.onDropdownVisibleChange}
                                                size={condensed ? 'middle' : 'large'}
                                                condensed={condensed}
                                                mode={selectProps.mode}
                                                getPopupContainer={
                                                    !!selectProps?.getPopupContainer
                                                        ? selectProps?.getPopupContainer
                                                        : (trigger) => trigger.parentElement
                                                }
                                                onPopupScroll={handlePopupScroll}
                                                dropdownRender={(codelist) => (
                                                    <>
                                                        {codelist}
                                                        {!hideCodelistMenu && (
                                                            <>
                                                                <Divider style={{ margin: '8px 0' }} />
                                                                <Space style={{ padding: '0 8px 4px' }}>
                                                                    <SearchCustomerButton
                                                                        loading={loading}
                                                                        disabled={fillButtonsDisabled}
                                                                        name="shows_err_w/o_name"
                                                                        condensed={condensed}
                                                                        title={'Codelist Menu'}
                                                                        onClick={handleShowCodelistMenuDrawer}
                                                                    />
                                                                </Space>
                                                            </>
                                                        )}
                                                        {declaration?.gvmsDeclaration &&
                                                            Object.values(GvmsFormCustomFields).some(
                                                                (customF) => customF === label
                                                            ) && (
                                                                <>
                                                                    <Divider style={{ margin: '8px 0' }} />
                                                                    <Space style={{ padding: '0 8px 4px' }}>
                                                                        <GvmsCustomFieldsDrawerButton
                                                                            label={label as GvmsFormCustomFields}
                                                                            fieldPath={fieldPropsProp?.name}
                                                                        />
                                                                    </Space>
                                                                </>
                                                            )}
                                                    </>
                                                )}
                                            >
                                                {optionsRender}
                                            </StyledDeclarationSelect>
                                        </SourceIconPrefix>
                                    </div>

                                    {multipleF && <MultipleButtons {...multipleF} />}
                                    {multiple ? multiple.node : <></>}
                                    {template && (
                                        <TemplateCheckboxes
                                            required={required}
                                            fieldPath={fieldPropsProp?.name}
                                            specialName={specialName}
                                            onChange={handleTemplateCheckboxChange}
                                            disabled={disabled}
                                        />
                                    )}
                                </InputDiv>
                                <ErrorDiv
                                    error={!!fieldMeta?.error}
                                    touched={!!fieldMeta?.touched}
                                    condensed={condensed}
                                >
                                    {!!fieldMeta?.error && !!fieldMeta?.touched && (
                                        <ErrorLabel>{fieldMeta?.error}</ErrorLabel>
                                    )}
                                </ErrorDiv>
                            </FormItem>
                            {codelistMenuDrawer && codeValidation && (
                                <CodelistMenuDrawer
                                    codelistData={selectOptions}
                                    fieldLabel={label}
                                    fieldName={fieldPropsProp?.name}
                                    visible={codelistMenuDrawer}
                                    onClose={hideCodelistMenuDrawer}
                                    codeValidation={codeValidation}
                                />
                            )}
                        </InputSection>
                    </>
                )}
            </FieldWrapper>
        ),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [
            fieldMeta?.error,
            fieldMeta?.touched,
            fieldMeta?.value,
            fieldEvents?.onBlur,
            fieldEvents?.onChange,
            disabled,
            selectOptions,
            codelistMenuDrawer,
            viewOnly,
            hasMeta,
            fieldProps,
            multipleF?.canRemove,
            multipleF?.canAdd,
            loading,
            searchProps,
            optionsRender,
            sourceIcon,
        ]
    );

    if (hidden || (isInvisible && !template)) return null;

    return render;
};

export default DeclarationSelect;
